import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  TextField,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Divider,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  ShoppingCart,
  Send,
  Close
} from '@mui/icons-material';
import orderService from '../services/orderService';
import cartService from '../services/cartService';
import uploadService from '../services/uploadService';

const CheckoutDialog = ({ open, onClose, cart, onSuccess }) => {
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError('');

      // Preparar datos del pedido
      const orderData = {
        items: cart.items.map(item => ({
          productId: item.productId,
          quantity: item.quantity
        })),
        notes: notes.trim() || null
      };

      // Enviar pedido
      const result = await orderService.create(orderData);

      if (result.success) {
        // Limpiar carrito
        cartService.clearCart();
        
        // Notificar éxito
        if (onSuccess) {
          onSuccess(result.data);
        }
        
        // Cerrar diálogo
        onClose();
      } else {
        setError(result.message);
      }
    } catch (error) {
      console.error('Error al crear pedido:', error);
      setError('Error al crear el pedido. Por favor, inténtalo nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS'
    }).format(price || 0);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ShoppingCart color="primary" />
          <Typography variant="h6">
            Confirmar Pedido
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Typography variant="subtitle2" gutterBottom>
          Resumen del Pedido
        </Typography>

        <List sx={{ bgcolor: 'background.paper', borderRadius: 1, border: 1, borderColor: 'divider', mb: 2 }}>
          {cart.items.map((item, index) => (
            <React.Fragment key={item.productId}>
              <ListItem>
                <ListItemAvatar>
                  <Avatar
                    variant="rounded"
                    src={item.image ? uploadService.getImageUrl(item.image) : '/no-image.svg'}
                    alt={item.name}
                  />
                </ListItemAvatar>
                <ListItemText
                  primary={item.name}
                  secondary={
                    <Box component="div">
                      <Typography variant="caption" display="block" component="span">
                        Cantidad: {item.quantity}
                      </Typography>
                      <Typography variant="caption" display="block" component="span">
                        Precio unitario: {formatPrice(item.price)}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500, mt: 0.5 }} component="div">
                        Subtotal: {formatPrice(item.price * item.quantity)}
                      </Typography>
                    </Box>
                  }
                  secondaryTypographyProps={{ component: 'div' }}
                />
              </ListItem>
              {index < cart.items.length - 1 && <Divider component="li" />}
            </React.Fragment>
          ))}
        </List>

        <Box sx={{ mb: 2, p: 2, bgcolor: 'primary.light', borderRadius: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Total:
            </Typography>
            <Typography variant="h5" color="primary" sx={{ fontWeight: 700 }}>
              {formatPrice(cart.total)}
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary">
            {cart.items.reduce((sum, item) => sum + item.quantity, 0)} item(s) en total
          </Typography>
        </Box>

        <TextField
          fullWidth
          multiline
          rows={3}
          label="Notas o comentarios (opcional)"
          placeholder="Ej: Necesito factura A, entregar antes del viernes..."
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          disabled={loading}
        />

        <Alert severity="info" sx={{ mt: 2 }}>
          Tu pedido será enviado para revisión. Un vendedor lo procesará y te contactará pronto.
        </Alert>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onClose}
          disabled={loading}
          startIcon={<Close />}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : <Send />}
        >
          {loading ? 'Enviando...' : 'Enviar Pedido'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CheckoutDialog;

